<template>
    <filters-wrapper :has-active-filters="props.hasActiveFilters" @submit="emit('submit')" @reset="emit('reset')">
        <template v-slot:panel>
            <FilterStatusToggle v-model="filters.active" @update:model-value="emit('submit')" />
        </template>
        <v-form class="table-filters">
            <v-row dense>
                <v-col cols="12" md="3" sm="6">
                    <v-select
                        v-model="filters.type"
                        :items="getCompanyTypeItems()"
                        :label="$t('type')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="company-type"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <daterange
                        :start-date-label="`${$t('registration')} ${$t('date_from')}`"
                        :end-date-label="$t('date_to')"
                        :dense="true"
                        :variant="`outlined`"
                        :single-line="false"
                        :hide-details="false"
                        :min-start-date="null"
                        same-date
                        :_start-date="filters.rangeFrom"
                        :_end-date="filters.rangeTo"
                        data-cy="date-range"
                        :clearable="true"
                        @change-start-date="filters.rangeFrom = $event"
                        @change-end-date="filters.rangeTo = $event"
                    />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" md="3" sm="6">
                    <v-autocomplete
                        v-model="filters.countryId"
                        :items="useCountries"
                        :label="$t('country')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="country-id"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-text-field
                        v-model="filters.name"
                        :label="$t('name')"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="company-name"
                    />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" md="3" sm="6">
                    <v-autocomplete
                        ref="cityRef"
                        v-model="filters.cityId"
                        :items="cities"
                        :label="$t('city')"
                        :disabled="isCitiesLoading || !cities.length"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="city-id"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-text-field
                        v-model="filters.companyCodeOrAlias"
                        :label="$t('company_code_or_alias')"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="alias"
                    />
                </v-col>
            </v-row>
        </v-form>
    </filters-wrapper>
</template>

<script setup lang="ts">
    import type {CompanyFiltersType} from '~/utils/types/account/companies/types'
    import {initCompanyFilters} from '~/utils/types/account/companies/helpers'
    import {DataRange, type SelectOptionType} from '~/utils/types/types'
    import Daterange from '~/components/search/forms/Daterange.vue'
    import {useCountries} from '~/composables/form-helpers'

    const props = defineProps<{
        hasActiveFilters: boolean
        companyType: string[]
    }>()
    const filters = defineModel<CompanyFiltersType>({
        default: initCompanyFilters(),
    })
    const emit = defineEmits<{
        (e: 'submit' | 'reset'): void
    }>()

    const isCitiesLoading = ref<boolean>(true)
    const cities = ref<SelectOptionType[]>([])
    const cityRef = ref()

    const app = useNuxtApp()
    const {t} = useI18n()

    watch(
        () => filters.value.countryId,
        async (): Promise<void> => {
            if (filters.value.cityId) cityRef.value.reset()
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            filters.value.countryId ? await loadCities() : (cities.value = [])
        },
        {deep: true}
    )

    watch(useCountries, async () => {
        if (filters.value.countryId) await loadCities()
    })

    async function loadCities(): Promise<void> {
        try {
            isCitiesLoading.value = true
            const rs = await app.$api.locations.get({
                limitCities: -1,
                orderBy: 'POPULARITY',
                countryId: filters.value.countryId,
            })
            if (rs.cities) {
                cities.value = rs.cities.map(({id, name}) => {
                    return {title: name, value: id}
                })
            }
        } catch (e) {
            cities.value = []
        } finally {
            isCitiesLoading.value = false
        }
    }

    onMounted(async () => {
        filters.value.rangeType = DataRange.Registration
    })

    function getCompanyTypeItems(): SelectOptionType[] {
        const companyTypes = props.companyType
        return companyTypes.map(value => ({
            value,
            title: t(`companyTypes.${value}`),
        }))
    }
</script>
